import apiService from './api';
import config from '../config/config';
import { STORAGE_KEYS, USER_TYPES } from '../constants/constants';

class AuthService {
  // Client login
  async clientLogin(email, password, rememberMe = false) {
    try {
      const response = await apiService.clientLogin(email, password);

      if (response.success && response.data) {
        this.handleLoginSuccess(response.data, rememberMe);
        return response;
      }

      throw new Error(response.message || 'Login failed');
    } catch (error) {
      // api.js now throws a clean error with err.code = 'SESSION_LIMIT_REACHED'
      // directly from raw axios — just bubble it up unchanged
      // console.log('[authService.clientLogin] caught:', error?.code, error?.message);
      if (error?.code === 'SESSION_LIMIT_REACHED') throw error;
      throw this.handleAuthError(error);
    }
  }

  // Client User login
  async clientUserLogin(email, password, rememberMe = false) {
    try {
      const response = await apiService.clientUserLogin(email, password);

      if (response.success && response.data) {
        const loginData = {
          token: response.data.token,
          user: response.data.user,
          license: response.data.license || null,
          branding: response.data.parentClient ? {
            company_name: response.data.parentClient.company_name,
            primary_color: response.data.parentClient.primary_color,
            accent_color: response.data.parentClient.accent_color
          } : null
        };
        this.handleLoginSuccess(loginData, rememberMe);
        return response;
      }

      throw new Error(response.message || 'Login failed');
    } catch (error) {
      // console.log('[authService.clientUserLogin] caught:', error?.code, error?.message);
      if (error?.code === 'SESSION_LIMIT_REACHED') throw error;
      throw this.handleAuthError(error);
    }
  }

  // Handle successful login
  handleLoginSuccess(data, rememberMe = false) {
    const { token, user, license, branding } = data;

    // Store authentication token
    apiService.setAuthToken(token);

    // Store user data with additional context
    const userData = {
      ...user,
      license: license || null,
      branding: branding || null,
      loginTime: new Date().toISOString(),
      userType: user.client_id ? USER_TYPES.CLIENT_USER : USER_TYPES.CLIENT,
    };

    apiService.setCurrentUser(userData);

    // Store remember me preference
    if (rememberMe) {
      localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
    } else {
      localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
    }

    return userData;
  }

  // Logout
  async logout() {
    try {
      // Call logout endpoint to invalidate server session
      await apiService.logout();
    } catch (error) {
      // Continue with local logout even if server call fails
      // console.warn('Server logout failed:', error.message);
    } finally {
      // Clear local authentication data
      apiService.clearAuth();
    }
  }

  // Verify token
  async verifyToken() {
    try {
      const response = await apiService.verifyToken();

      if (response.success && response.data) {
        // Update user data with fresh information
        let userData;
        if(response?.data?.data) {
          userData = response.data.data;
        } else {
          userData = response.data;
        }

        // For client users, ensure branding is properly extracted
        if (userData.user && userData.user.client_id) {
          // This is a client user - ensure branding is set
          if (!userData.branding && userData.parentClient) {
            userData.branding = {
              company_name: userData.parentClient.company_name,
              primary_color: userData.parentClient.primary_color,
              accent_color: userData.parentClient.accent_color
            };
          }
        }

        apiService.setCurrentUser(userData);
        return userData;
      }

      throw new Error('Token verification failed');
    } catch (error) {
      // Clear invalid authentication
      apiService.clearAuth();
      throw this.handleAuthError(error);
    }
  }

  // Refresh token
  async refreshToken() {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiService.refreshToken(refreshToken);

      if (response.success && response.data) {
        const { token, user } = response.data;
        apiService.setAuthToken(token);
        apiService.setCurrentUser(user);
        return response.data;
      }

      throw new Error('Token refresh failed');
    } catch (error) {
      // Clear authentication on refresh failure
      apiService.clearAuth();
      throw this.handleAuthError(error);
    }
  }

  // Get refresh token (if stored separately)
  getRefreshToken() {
    // If refresh tokens are stored separately, implement here
    // For now, assuming refresh is handled via HTTP-only cookies
    return null;
  }

  // Change password
  async changePassword(oldPassword, newPassword) {
    try {
      const response = await apiService.changePassword(oldPassword, newPassword);
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  // Forgot password
  async forgotPassword(email, userType = USER_TYPES.CLIENT) {
    try {
      const response = await apiService.post('/auth/forgot-password', {
        email,
        userType,
      });

      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  // Reset password
  async resetPassword(token, newPassword, userType = USER_TYPES.CLIENT) {
    try {
      const response = await apiService.post('/auth/reset-password', {
        token,
        newPassword,
        userType,
      });

      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  // Check authentication status
  isAuthenticated() {
    return apiService.isAuthenticated();
  }

  // Get current user
  getCurrentUser() {
    return apiService.getCurrentUser();
  }

  // Set current user
  setCurrentUser(userData) {
    return apiService.setCurrentUser(userData);
  }

  // Get user type
  getUserType() {
    const user = this.getCurrentUser();
    return user?.userType || null;
  }

  // Check if user is client
  isClient() {
    return this.getUserType() === USER_TYPES.CLIENT;
  }

  // Check if user is client user
  isClientUser() {
    return this.getUserType() === USER_TYPES.CLIENT_USER;
  }

  // Get user permissions
  getUserPermissions() {
    const user = this.getCurrentUser();
    return user?.permissions || {};
  }

  // Check specific permission
  hasPermission(permission) {
    const permissions = this.getUserPermissions();
    // console.log('permissioon--',permission)
    return permissions[permission] === true;
  }

  // Get user branding
  getUserBranding() {
    const user = this.getCurrentUser();
    return user?.branding || null;
  }

  // Get user license
  getUserLicense() {
    const user = this.getCurrentUser();
    return user?.license || null;
  }

  // Get search quota information
  getSearchQuota() {
    const user = this.getCurrentUser();
    const license = this.getUserLicense();

    
    // Handle case when user is not available
    if (!user) {
      return {
        daily: 0,
        monthly: 0,
        allowedTypes: [],
      };
    }
    
    
    if (user.user && typeof user.user.userType != "undefined" && user.user.userType === USER_TYPES.CLIENT_USER) {
      return {
        daily: user.user.search_quota_daily || 0,
        monthly: user.user.search_quota_monthly || 0,
        allowedTypes: user.user.search_types_allowed || [],
      };
    } else if (user.userType === USER_TYPES.CLIENT_USER) {
      return {
        daily: user.search_quota_daily || 0,
        monthly: user.search_quota_monthly || 0,
        allowedTypes: user.search_types_allowed || [],
      };
    }

    if (user.user && typeof user.user.userType != "undefined" && user.user.userType === USER_TYPES.CLIENT && license) {
      // console.log("license: ", license);
      return {
        daily: license.search_quota_daily || 0,
        monthly: license.search_quota_monthly || 0,
        allowedTypes: license.search_types_allowed || [],
      };
    } else if (user.userType === USER_TYPES.CLIENT && license) {
      // console.log("license: ", license);
      return {
        daily: license.search_quota_daily || 0,
        monthly: license.search_quota_monthly || 0,
        allowedTypes: license.search_types_allowed || [],
      };
    }

    return {
      daily: 0,
      monthly: 0,
      allowedTypes: [],
    };
  }

  // Check if search type is allowed
  isSearchTypeAllowed(searchType) {
    const quota = this.getSearchQuota();
    // console.log("quota: ", quota);
    // console.log("quota allowed types: ", quota.allowedTypes);
    // console.log("searchType: ", searchType);

    // Special case: if tm_number is allowed, then tm_numbers should also be allowed
    // if (searchType === 'tm_numbers') {
    //   return quota.allowedTypes.includes('tm_number');
    // }

    return quota.allowedTypes.includes(searchType);
  }

  // Handle authentication errors
handleAuthError(error) {

  const message =
    error?.response?.data?.message ||
    error?.response?.data?.error   ||
    error?.message                  ||
    'Authentication failed';

  const authError = new Error(message);

  // Preserve status from both axios errors (.response.status)
  // and our clean api.js errors (.status)
  authError.code    = error?.response?.status || error?.status || null;
  authError.status  = error?.response?.status || error?.status || null;
  authError.details = error?.response?.data   || error?.responseData || null;

  return authError;
}

  // Auto-logout after session timeout
  startSessionTimer() {
    // Clear existing timer
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
    }

    // Set new timer
    this.sessionTimer = setTimeout(() => {
      this.handleSessionTimeout();
    }, config.auth.sessionTimeout);
  }

  // Handle session timeout
  handleSessionTimeout() {
    this.logout();
    // Notify user about session expiration
    window.dispatchEvent(new CustomEvent('sessionTimeout'));
  }

  // Stop session timer
  stopSessionTimer() {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = null;
    }
  }

  // Reset session timer on user activity
  resetSessionTimer() {
    if (this.isAuthenticated()) {
      this.startSessionTimer();
    }
  }
}

// Create and export singleton instance
const authService = new AuthService();
export default authService;